import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
import { TRIAL_DAYS_MAX, TRIAL_DAYS_MIN } from './trial-days.js';
/**
 * Account type — legal form (admin-prd §6.5 «Реквизиты организации»).
 * `legal_entity` (юрлицо), `sole_proprietor` (ИП), `individual` (физлицо).
 */
export const accountTypeSchema = z.enum([
    'legal_entity',
    'sole_proprietor',
    'individual',
]);
/**
 * Where an account stands, derived from its current subscription — the latest
 * `customers.subscriptions` row by `(created_at, id)` — against the server's
 * today, the UTC date (registration-approval arch §2.3). Nothing stores it;
 * `customers.accounts.status` is not read for it.
 *
 * - `active` — paid: `active`, and `past_due` / `suspended`, which nothing
 *   writes today (PRD F-10);
 * - `trial` — a trial whose `period_end` is after today;
 * - `trial_ended` — a trial whose `period_end` is today or earlier: «Триал
 *   истёк». It still plays until a manager stops it (ADR-044);
 * - `paused`, `cancelled`, `archived` — the subscription's own status;
 * - `none` — no subscription, or a status this list does not know.
 */
export const accountSubscriptionStateSchema = z.enum([
    'active',
    'trial',
    'trial_ended',
    'paused',
    'cancelled',
    'archived',
    'none',
]);
/**
 * Billing mode (admin-prd §6.5). Режим A (manual) — bookkeeper pays via bank
 * transfer, admin extends `paid_until` manually. Режим B (self-service) —
 * online gateway with stored cards + autorenewal; coded but disabled in MVP
 * production.
 */
export const accountBillingModeSchema = z.enum(['manual', 'self_service']);
/**
 * Account — top-level customer entity (admin-arch customers.accounts + PRD §6).
 * `head_venue_id` is reserved per F-18 for the v1.1 multi-venue model and is
 * NULL in MVP except where the single venue has been created.
 */
export const accountSchema = z.object({
    id: uuidSchema,
    name: z.string().min(1).max(255),
    type: accountTypeSchema,
    primary_contact_email: z.string().email().max(255),
    primary_contact_name: z.string().min(1).max(255),
    region: z.string().max(100).nullable(),
    manager_user_id: uuidSchema.nullable(),
    head_venue_id: uuidSchema.nullable(),
    created_at: isoDateTimeSchema,
    archived_at: isoDateTimeSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Wave 2.12 BE-2 (TASK-070a) — accounts list / profile / org-details surface.
// ---------------------------------------------------------------------------
/**
 * One row in `GET /admin/accounts` (admin-prd §6.2, admin-arch §3.6).
 *
 * `tariff_id` / `tariff_code` / `billing_mode` / `paid_until` are populated
 * from the LATEST `customers.subscriptions` row for the account (joined
 * service-side). All four are nullable — an account without any subscription
 * (rare; freshly onboarded prior to picking a tariff) renders as a row with
 * blank tariff cell on the FE.
 *
 * `venues_count` is a server-computed integer (single LEFT JOIN COUNT
 * subquery — no N+1). FE colors `paid_until < NOW()` client-side; the server
 * does not pre-compute "overdue" so the response stays clean.
 *
 * `state` comes from that same latest subscription
 * ({@link accountSubscriptionStateSchema}); it replaced the account `status`
 * in v1.3.0 (STORY-013). For a trial, `paid_until` is the `period_end` date,
 * `YYYY-MM-DD`: the trial's last day is the day before it (`lastDayOf`).
 */
export const accountListItemSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    type: accountTypeSchema,
    state: accountSubscriptionStateSchema,
    tariff_id: uuidSchema.nullable(),
    tariff_code: z.string().nullable(),
    billing_mode: accountBillingModeSchema,
    paid_until: z.string().nullable(),
    venues_count: z.number().int().nonnegative(),
    primary_contact_email: z.string(),
    region: z.string().nullable(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
});
export const accountListResponseSchema = z.object({
    items: z.array(accountListItemSchema),
    total: z.number().int().nonnegative().nullable(),
    next_cursor: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Filter / pagination schema (admin-arch §3.6).
//
// Cursor pattern mirrors `auditLogFilterSchema` / `trackFilterSchema` so the
// wire conventions stay uniform. CSV fan-out for `state` accepts either
// an already-parsed array (Express has parsed `?state=a&state=b` syntax)
// or a single comma-joined string.
// ---------------------------------------------------------------------------
const Q_MAX = 200;
const REGION_MAX = 100;
const CURSOR_MAX = 200;
const STATE_IN_MAX = accountSubscriptionStateSchema.options.length;
const csvStateArraySchema = z.preprocess((raw) => {
    if (raw === undefined || raw === null || raw === '')
        return undefined;
    if (Array.isArray(raw))
        return raw;
    if (typeof raw === 'string') {
        return raw
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
    }
    return raw;
}, z.array(accountSubscriptionStateSchema).max(STATE_IN_MAX).optional());
export const accountFilterSchema = z.object({
    q: z.string().min(1).max(Q_MAX).optional(),
    /**
     * Accounts whose {@link accountSubscriptionStateSchema} is one of these
     * (arch §7): «Активные» `active`, «Триал» `trial,trial_ended`, «Архив»
     * `archived`, the «Триал истёк» facet `trial_ended`; «Все» sends none.
     * It replaced `status` in v1.3.0.
     */
    state: csvStateArraySchema,
    tariff_id: uuidSchema.optional(),
    paid_until_before: isoDateTimeSchema.optional(),
    paid_until_after: isoDateTimeSchema.optional(),
    region: z.string().min(1).max(REGION_MAX).optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
    cursor: z.string().max(CURSOR_MAX).optional(),
});
// ---------------------------------------------------------------------------
// Account card (admin-prd §6.3) — GET /admin/accounts/:id
// ---------------------------------------------------------------------------
/**
 * Organization details JSON shape (admin-prd §6.5 — ИНН / КПП / реквизиты).
 * All fields optional — blank for individuals (физлица), required for legal
 * entities before the first invoice is generated.
 *
 * `inn` length 10 (legal entity) or 12 (individual / sole proprietor); we
 * validate length only, not check digit, to keep the boundary forgiving.
 */
export const accountOrgDetailsSchema = z
    .object({
    legal_name: z.string().max(500).nullable().optional(),
    inn: z
        .string()
        .regex(/^\d{10}(\d{2})?$/, 'INN must be 10 or 12 digits')
        .nullable()
        .optional(),
    kpp: z
        .string()
        .regex(/^\d{9}$/, 'KPP must be 9 digits')
        .nullable()
        .optional(),
    ogrn: z
        .string()
        .regex(/^\d{13}(\d{2})?$/, 'OGRN must be 13 or 15 digits')
        .nullable()
        .optional(),
    legal_address: z.string().max(500).nullable().optional(),
    bank_name: z.string().max(255).nullable().optional(),
    bank_bik: z
        .string()
        .regex(/^\d{9}$/, 'BIK must be 9 digits')
        .nullable()
        .optional(),
    bank_account: z
        .string()
        .regex(/^\d{20}$/, 'Bank account must be 20 digits')
        .nullable()
        .optional(),
    billing_email: z.string().email().max(255).nullable().optional(),
})
    .strict();
/**
 * Tariff snapshot embedded in `GET /admin/accounts/:id`. Resolved via JOIN
 * to `internal.tariffs` on the latest subscription's `tariff_id`.
 */
export const accountCardTariffSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    name: z.string(),
    monthly_price_kop: z.number().int().nonnegative(),
});
/**
 * Latest subscription snapshot embedded in `GET /admin/accounts/:id` — the
 * account's current subscription, the latest row by `(created_at, id)`, the
 * same row the list reads. `paid_until` mirrors `subscriptions.period_end`
 * (date, not timestamptz — billing is day-grain per admin-arch §6.5); for a
 * trial its last day is the day before (`lastDayOf`).
 *
 * `state` ({@link accountSubscriptionStateSchema}) is where the account stands
 * by this subscription, judged on the server's today: the header badge,
 * «Обзор» and «Подписки» read it. It replaced `account.status` in v1.3.0
 * (STORY-014). `status` stays the subscription's own status, which the
 * lifecycle actions are allowed by.
 *
 * `stopped_as_trial` says whether this subscription, cancelled or archived,
 * was a trial when it stopped (registration-approval arch §2.2, STORY-017):
 * «Отменить» writes it from the status it cancelled, «Архивировать» a trial
 * sets it, archiving a cancelled one keeps it, an activation clears it. A live
 * subscription reads `false` — a live trial says so with `status`. Rows
 * stopped before v1.3.0 got it from the journal (migration 0129).
 *
 * `monthly_amount_kop` is this subscription's monthly price snapshot (ADR-010)
 * — the price a recorded payment counts whole months at (registration-approval
 * arch §9.1, STORY-019), which may differ from the tariff's price today
 * (`tariff.monthly_price_kop`).
 */
export const accountCardSubscriptionSchema = z.object({
    id: uuidSchema,
    status: z.string(),
    state: accountSubscriptionStateSchema,
    stopped_as_trial: z.boolean(),
    started_at: isoDateTimeSchema,
    paid_until: z.string().nullable(),
    monthly_amount_kop: z.number().int().nonnegative(),
});
export const accountCardResponseSchema = z.object({
    account: z.object({
        id: uuidSchema,
        name: z.string(),
        type: accountTypeSchema,
        billing_mode: accountBillingModeSchema,
        primary_contact_email: z.string(),
        /**
         * Surfaced Wave 2.56 BE-2 (TASK-236-followup-be) once
         * `customers.accounts.contact_name` migration 0055 landed. Nullable so
         * accounts created before the column existed (NULL backfill) keep
         * round-tripping cleanly.
         */
        primary_contact_name: z.string().nullable(),
        primary_contact_phone: z.string().nullable(),
        region: z.string().nullable(),
        manager_user_id: uuidSchema.nullable(),
        /**
         * Denormalised display name of the assigned account manager (joined from
         * `internal.users.name`). Surfaced Wave 2.58 BE
         * (TASK-299-followup-be-team-name-denorm) so the FE Overview tab can
         * render «Менеджер music_app» without a per-row N+1 fetch.
         *
         * `null` when:
         *   - `manager_user_id` is itself NULL (no manager assigned), OR
         *   - the joined `internal.users` row is soft-deleted / hard-deleted
         *     (FK is `ON DELETE SET NULL` on `customers.accounts.manager_user_id`
         *     so the second case mostly degrades to the first).
         */
        manager_name: z.string().nullable(),
        channel: z.string().nullable(),
        head_venue_id: uuidSchema.nullable(),
        organization_details: accountOrgDetailsSchema.nullable(),
        cancelled_at: isoDateTimeSchema.nullable(),
        created_at: isoDateTimeSchema,
        updated_at: isoDateTimeSchema,
    }),
    tariff: accountCardTariffSchema.nullable(),
    current_subscription: accountCardSubscriptionSchema.nullable(),
    venues_count: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// PATCH bodies (admin-prd §6.3 / §6.5).
//
// `.strict()` blocks unknown keys so a typo (`tarif_id` instead of
// `tariff_id`) surfaces as 400 rather than silently dropping.
// Empty body → 400 via `.refine()` (zero-diff PATCH would be a no-op but
// also a hint the caller misunderstood the contract).
// ---------------------------------------------------------------------------
export const accountProfilePatchSchema = z
    .object({
    name: z.string().trim().min(1).max(255).optional(),
    type: accountTypeSchema.optional(),
    primary_contact_email: z.string().email().max(255).optional(),
    /**
     * Surfaced Wave 2.56 BE-2 (TASK-236-followup-be) once the
     * `customers.accounts.contact_name` column landed (migration 0055).
     * Patch payload allows blank `null` (FE clears the field) but not an
     * empty string — same semantics as `primary_contact_phone` / `region`.
     */
    primary_contact_name: z.string().trim().min(1).max(255).nullable().optional(),
    primary_contact_phone: z.string().max(64).nullable().optional(),
    region: z.string().max(100).nullable().optional(),
    manager_user_id: uuidSchema.nullable().optional(),
})
    .strict()
    .refine((obj) => Object.keys(obj).length > 0, 'At least one field must be provided');
// ---------------------------------------------------------------------------
// POST /admin/accounts — create body (Wave 2.56 BE-2, TASK-236-followup-be).
//
// Wire shape mirrors the fields the Wave 2.42 FE shell already collects
// (`apps/admin/src/api/account-create.ts`), plus an optional `manager_user_id`
// that will be filled in once the FE follow-up adds the manager picker
// (powered by `GET /admin/users`). The two contact fields are required to
// match `customers.accounts.contact_email` / `contact_name` NOT NULL columns;
// `primary_contact_phone`, `region`, `tariff_id`, `manager_user_id` are all
// nullable to match the DB shape.
//
// `.strict()` blocks unknown keys so a typo (`tarif_id` instead of
// `tariff_id`) surfaces as 400 rather than silently dropping.
//
// A tariff starts the account on a trial of `trial_days` (STORY-016,
// registration-approval arch §8): the account works at once, no approval.
//
// Response: the same composite shape `GET /admin/accounts/:id` returns
// (`accountCardResponseSchema`) so the FE can navigate to `/accounts/<id>`
// and render the freshly-created card without a second round trip.
// ---------------------------------------------------------------------------
export const accountCreateRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(255),
    type: accountTypeSchema,
    primary_contact_email: z.string().trim().email().max(255),
    primary_contact_name: z.string().trim().min(1).max(255),
    primary_contact_phone: z.string().trim().max(64).nullable().optional(),
    region: z.string().trim().max(100).nullable().optional(),
    tariff_id: uuidSchema.nullable().optional(),
    manager_user_id: uuidSchema.nullable().optional(),
    /**
     * Trial length in days, `TRIAL_DAYS_MIN`–`TRIAL_DAYS_MAX` (PRD A-3); only
     * with a tariff. The trial runs from the server's UTC today: the
     * subscription's `period_end` (the card's `paid_until`) is today +
     * `trial_days`, its last day the day before (`lastDayOf`). Left out → 14
     * days (`TRIAL_DAYS_DEFAULT`), which is also what an admin tab still on the
     * v1.2 SPA gets. Sent without a tariff (left out or `null`) → `400
     * VALIDATION_ERROR` on `trial_days`: an account without a tariff gets no
     * trial (A-8). Not nullable — leave it out instead.
     */
    trial_days: z.number().int().min(TRIAL_DAYS_MIN).max(TRIAL_DAYS_MAX).optional(),
})
    .strict()
    .refine((body) => body.trial_days === undefined || typeof body.tariff_id === 'string', {
    message: 'trial_days needs a tariff_id: an account without a tariff gets no trial',
    path: ['trial_days'],
});
export const accountOrgDetailsPatchSchema = z
    .object({
    legal_name: z.string().trim().max(500).nullable().optional(),
    inn: z
        .string()
        .trim()
        .regex(/^\d{10}(\d{2})?$/, 'INN must be 10 or 12 digits')
        .nullable()
        .optional(),
    kpp: z
        .string()
        .trim()
        .regex(/^\d{9}$/, 'KPP must be 9 digits')
        .nullable()
        .optional(),
    ogrn: z
        .string()
        .trim()
        .regex(/^\d{13}(\d{2})?$/, 'OGRN must be 13 or 15 digits')
        .nullable()
        .optional(),
    legal_address: z.string().trim().max(500).nullable().optional(),
    bank_name: z.string().trim().max(255).nullable().optional(),
    bank_bik: z
        .string()
        .trim()
        .regex(/^\d{9}$/, 'BIK must be 9 digits')
        .nullable()
        .optional(),
    bank_account: z
        .string()
        .trim()
        .regex(/^\d{20}$/, 'Bank account must be 20 digits')
        .nullable()
        .optional(),
    billing_email: z.string().trim().email().max(255).nullable().optional(),
})
    .strict()
    .refine((obj) => Object.keys(obj).length > 0, 'At least one field must be provided');
// ---------------------------------------------------------------------------
// Wave 2.13 BE-1 (TASK-070b) — team / venues / audit / notes surface.
// ---------------------------------------------------------------------------
/**
 * Roles a tenant-side team member can hold (admin-prd §0 personas).
 * `client_owner` corresponds to "Владелец аккаунта", `client_manager` to
 * "Менеджер". One owner per account is enforced at the application layer.
 */
export const accountTeamRoleSchema = z.enum([
    'client_owner',
    'client_manager',
]);
/**
 * One row in `GET /admin/accounts/:id/team` (admin-prd §6.3 «Команда»).
 *
 * `status` covers the team member lifecycle:
 *   - `active`   — accepted invite, currently in the team
 *   - `invited`  — invite sent, not yet accepted (joined_at IS NULL)
 *   - `removed`  — soft-removed (is_active=false)
 * `can_manage` is server-computed convenience for the FE — true iff the
 * current actor (admin/super_admin) can mutate this row. Always true for
 * admin/super_admin callers in MVP; placeholder for future selector scope.
 */
export const accountTeamMemberStatusSchema = z.enum([
    'active',
    'invited',
    'removed',
]);
export const accountTeamMemberSchema = z.object({
    user_id: uuidSchema,
    email: z.string(),
    name: z.string(),
    role: accountTeamRoleSchema,
    status: accountTeamMemberStatusSchema,
    invited_at: isoDateTimeSchema.nullable(),
    last_login_at: isoDateTimeSchema.nullable(),
    can_manage: z.boolean(),
});
export const accountTeamListResponseSchema = z.object({
    items: z.array(accountTeamMemberSchema),
});
/**
 * `POST /admin/accounts/:id/team/invite` request body. Reuses the
 * Wave 2.11 admin invite token plumbing (sha256 hash, 48h TTL, single-use
 * consume), scoped to the account via `admin_invites.account_id`.
 */
export const accountTeamInviteSchema = z
    .object({
    email: z.string().trim().email().max(255),
    name: z.string().trim().min(1).max(255),
    role: accountTeamRoleSchema,
})
    .strict();
export const accountTeamInviteResponseSchema = z.object({
    invite_id: uuidSchema,
    expires_at: isoDateTimeSchema,
    invite_link_dev_only: z.string().url().optional(),
});
/**
 * One row in `GET /admin/accounts/:id/venues` (admin-prd §6.3 «Заведения»).
 *
 * `code` is reserved for a future per-venue short identifier (currently
 * NULL — the venues table has no `code` column). `format_id` / `format_name`
 * resolve via LEFT JOIN to `internal.venue_formats`; both NULL when the
 * venue has no format assigned.
 */
export const accountVenueListItemSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    code: z.string().nullable(),
    format_id: uuidSchema.nullable(),
    format_name: z.string().nullable(),
    timezone: z.string(),
    status: z.string(),
    created_at: isoDateTimeSchema,
});
export const accountVenueListResponseSchema = z.object({
    items: z.array(accountVenueListItemSchema),
});
/**
 * One row in `GET /admin/accounts/:id/notes` (admin-prd §6.3 «Заметки»).
 *
 * `author_user_id` is nullable: when the original admin author is
 * hard-deleted, the FK `ON DELETE SET NULL` nulls the column. `author_name`
 * is also nullable — it falls back to `null` whenever the user row is gone
 * or the JOIN was skipped (POST response path).
 */
export const accountNoteSchema = z.object({
    id: uuidSchema,
    account_id: uuidSchema,
    author_user_id: uuidSchema.nullable(),
    author_name: z.string().nullable(),
    content: z.string(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
});
export const accountNoteListResponseSchema = z.object({
    items: z.array(accountNoteSchema),
});
const NOTE_CONTENT_MAX = 5000;
export const accountNoteCreateSchema = z
    .object({
    content: z.string().trim().min(1).max(NOTE_CONTENT_MAX),
})
    .strict();
export const accountNotePatchSchema = z
    .object({
    content: z.string().trim().min(1).max(NOTE_CONTENT_MAX),
})
    .strict();
// ---------------------------------------------------------------------------
// BUG-081 / BUG-082 — account-level force-logout
// (`POST /admin/accounts/:id/force-logout`).
//
// Revokes every active `internal.user_sessions` row for users linked to the
// account via `customers.account_users`. Idempotent: a quiet account returns
// `revoked_sessions: 0`. Audit event `account.session_force_logout` is emitted
// once per call.
// ---------------------------------------------------------------------------
export const accountForceLogoutRequestSchema = z
    .object({
    reason: z.string().min(1).max(500).optional(),
})
    .strict();
export const accountForceLogoutResponseSchema = z.object({
    account_id: uuidSchema,
    revoked_sessions: z.number().int().nonnegative(),
    affected_users: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// GET /admin/accounts/:id/analytics — account-level analytics rollup
// (BUG-109). Mirrors the venue-analytics wire shape (totals + top_tracks +
// heatmap) but aggregates across ALL of the account's venues instead of a
// single venue. Reuses the same FE chart components the venue card mounts.
//
// Heatmap is bucketed by hour × day-of-week in UTC (an account can span
// multiple timezones, so no single venue clock applies). The 30-day default
// window matches the venue endpoint; `?from`/`?to` clamp to 90 days.
// ---------------------------------------------------------------------------
/** Query string of `GET /admin/accounts/:id/analytics`. */
export const accountAnalyticsQuerySchema = z
    .object({
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
})
    .strict();
/** Top-N track row of the account rollup. Identical shape to the venue one. */
export const accountAnalyticsTopTrackSchema = z.object({
    track_id: uuidSchema,
    title: z.string(),
    artist: z.string().nullable(),
    plays: z.number().int().nonnegative(),
});
/**
 * Wire DTO for `GET /admin/accounts/:id/analytics`. `venues_count` lets the FE
 * distinguish "account has no venues" from "venues exist but no plays yet".
 */
export const accountAnalyticsResponseSchema = z.object({
    window: z.object({
        from: isoDateTimeSchema,
        to: isoDateTimeSchema,
    }),
    venues_count: z.number().int().nonnegative(),
    totals: z.object({
        plays: z.number().int().nonnegative(),
        unique_tracks: z.number().int().nonnegative(),
        hours_played: z.number().nonnegative(),
        skip_rate: z.number().min(0).max(1).optional(),
    }),
    top_tracks: z.array(accountAnalyticsTopTrackSchema).max(10),
    heatmap: z.record(z.string().regex(/^([0-9]|1[0-9]|2[0-3])$/, 'hour 0..23'), z.record(z.string().regex(/^[0-6]$/, 'dow 0..6'), z.number().int().nonnegative())),
});
//# sourceMappingURL=account.js.map